import { useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./DocumentationUploadModal.module.scss";
import Modal from "../../../../../../components/common/Modal/Modal";
import TextInput from "../../../../../../components/forms/textInput/textInput";
import ImageUpload from "../../../../../../components/forms/imageUpload/ImageUpload";
import PrimaryButton from "../../../../../../components/common/PrimaryButton/PrimaryButton";
import OutlinedButton, {
  OutlinedButtonSize,
} from "../../../../../../components/common/OutlinedButton/OutlinedButton";
import PDFComponent from "../../../../../../components/common/PDFComponent/PDFComponent";
import { uploadFileWithPresignedUrl } from "../../../../../../s3Integration/s3Client";
import { EUploadImageType } from "../../../../../../s3Integration/enums";
import { addOrganizationDocumentation } from "../../../../../../setup/api/services/organization";
import { toast } from "react-toastify";
import Spinner from "../../../../../../components/common/Spinner";

interface Props {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  organizationCode: string;
}

interface DynamicRow {
  id: string;
  title: string;
  file: File | null;
  loading: boolean;
}

const DocumentationUploadModal = ({
  visible,
  onCancel,
  onSuccess,
  organizationCode,
}: Props) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<DynamicRow[]>([
    { id: Math.random().toString(), title: "", file: null, loading: false },
  ]);
  const [isSaving, setIsSaving] = useState(false);

  const handleAddMore = () => {
    setRows((prev) => [
      ...prev,
      { id: Math.random().toString(), title: "", file: null, loading: false },
    ]);
  };

  const handleRemoveRow = (id: string) => {
    if (rows.length === 1) {
      setRows([
        { id: Math.random().toString(), title: "", file: null, loading: false },
      ]);
      return;
    }
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const handleTitleChange = (id: string, value: string) => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, title: value } : row))
    );
  };

  const handleFileChange = (id: string, file: File | null) => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, file } : row))
    );
  };

  const handleSave = async () => {
    const validRows = rows.filter((row) => row.title && row.file);
    if (validRows.length === 0) {
      toast.error(t("please_add_at_least_one_valid_document"));
      return;
    }

    setIsSaving(true);
    try {
      const uploadPromises = validRows.map(async (row) => {
        if (!row.file) return;

        const uploadResult = await uploadFileWithPresignedUrl(
          row.file,
          EUploadImageType.ORGANIZATION,
          organizationCode
        );

        const body = {
          title: row.title,
          bucketName: uploadResult.bucket,
          objectName: uploadResult.key,
          description: row.file.name,
        };

        return addOrganizationDocumentation(organizationCode, body);
      });

      await Promise.all(uploadPromises);
      toast.success(t("documentation_saved_successfully"));
      onSuccess();
    } catch (error) {
      console.error("Error saving documentation:", error);
      toast.error(t("documentation_save_error"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal visible={visible} onCancel={onCancel} size="large">
      <div className={styles.modal_container}>
        <h3 className={styles.title}>{t("lbl_upload_documentation")}</h3>

        <div className={styles.dynamic_rows}>
          {rows.map((row) => (
            <div key={row.id} className={styles.row}>
              <div
                className={styles.remove_row}
                onClick={() => handleRemoveRow(row.id)}>
                <img src="/assets/images/svg/delete_logo.svg" alt="remove" />
              </div>

              <div className={styles.field}>
                <label>{t("lbl_document_title")}</label>
                <TextInput
                  value={row.title}
                  onChange={(e) => handleTitleChange(row.id, e.target.value)}
                  placeholder={t("enter_document_title")}
                />
              </div>

              <div className={styles.upload_field}>
                {row.file ? (
                  <div className={styles.uploaded_preview}>
                    {row.file.type === "application/pdf" ||
                    row.file.name.toLowerCase().includes(".pdf") ? (
                      <PDFComponent
                        fileUrl={URL.createObjectURL(row.file)}
                        fileName={row.file.name}
                        isPreview
                      />
                    ) : (
                      <img src={URL.createObjectURL(row.file)} alt="preview" />
                    )}
                    <div
                      className={styles.remove_row}
                      style={{ top: 4, right: 4 }}
                      onClick={() => handleFileChange(row.id, null)}>
                      <img
                        src="/assets/images/svg/delete_logo.svg"
                        alt="remove"
                      />
                    </div>
                  </div>
                ) : (
                  <ImageUpload
                    handleChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileChange(row.id, file);
                    }}
                    withPDF
                  />
                )}
              </div>
            </div>
          ))}
        </div>

        <div className={styles.add_more_container}>
          <OutlinedButton
            onClick={handleAddMore}
            width="fit-content"
            size={OutlinedButtonSize.SMALL}>
            <div className={styles.add_more_content}>
              <span>{t("lbl_add_more")}</span>
            </div>
          </OutlinedButton>
        </div>

        <div className={styles.button_container}>
          <OutlinedButton onClick={onCancel}>{t("lbl_cancel")}</OutlinedButton>
          <PrimaryButton onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Spinner /> : t("lbl_save")}
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
};

export default DocumentationUploadModal;
